import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Sparkles } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton, OutlineButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";
import { PROGRAMMES, IMAGES } from "@/lib/data";

export default function Courses() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative pt-40 pb-16 md:pt-52 md:pb-20 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Overline className="mb-5 block">Our Courses</Overline>
          <MaskedLines
            className="font-playfair uppercase leading-[0.95] tracking-tight text-white"
            lineClassName="text-6xl md:text-8xl"
            lines={[<span key="1">Train.</span>, <span key="2" className="text-gold">Perfect.</span>]}
          />
          <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
            Explore our regulated qualifications and accredited CPD training, delivered by experienced aesthetics practitioners and educators. Choose the pathway that&apos;s right for you.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PROGRAMMES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <Link
                to={p.path}
                data-testid={`programme-card-${p.id}`}
                className="group relative block h-[520px] md:h-[560px] overflow-hidden border border-white/10 hover:border-gold/40 transition-colors"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-navy/60" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/85 to-navy/20" />
                <div className="relative h-full p-8 md:p-12 flex flex-col justify-end">
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-gold mb-4">
                    {p.id === "cpd"
                      ? <><Sparkles size={16} strokeWidth={1.4} /> Accredited CPD</>
                      : <><BadgeCheck size={16} strokeWidth={1.4} /> Ofqual Regulated</>}
                  </span>
                  <h2 className="font-playfair text-4xl md:text-5xl text-white leading-tight">{p.name}</h2>
                  <p className="text-gold mt-3 text-[12px] tracking-[0.2em] uppercase">{p.tagline}</p>
                  <p className="text-white/70 mt-5 max-w-md leading-relaxed">{p.blurb}</p>
                  <span className="inline-flex items-center gap-2 mt-8 text-white group-hover:text-gold transition-colors text-sm tracking-[0.15em] uppercase">
                    {p.ctaLabel ? p.ctaLabel(p.count) : `View ${p.count} Qualifications`}
                    <ArrowUpRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-24 md:py-28 overflow-hidden border-t border-white/10">
        <div className="absolute inset-0">
          <img src={IMAGES.salon} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/90" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <Overline>Not sure where to start?</Overline>
            <h3 className="font-playfair text-3xl md:text-5xl text-white mt-5 leading-tight">
              We&apos;ll help you choose the right pathway
            </h3>
            <p className="text-white/60 mt-5 leading-relaxed">
              Our team is here to talk you through our accredited CPD courses, regulated qualifications, entry requirements and progression routes, helping you choose the most appropriate pathway for your existing experience and career goals.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <GoldButton onClick={() => openEnquiry()} data-testid="courses-enquire-button">Enquire Now</GoldButton>
              <OutlineButton onClick={() => openEnquiry()} data-testid="courses-callback-button">Request a Call-back</OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
